import {Hazard, Risk} from '@domain/hroc-checklists'
import {TextField} from '@hconnect/common/components'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {Stack} from '@mui/material'
import {useMemo} from 'react'
import {useFormContext} from 'react-hook-form'

import {ChecklistStepSection, AutocompleteField} from '../../components'
import {HROC_CHECKLIST_FORM_WIDTH_IN_PX} from '../../consts'
import {useWidgetTranslation} from '../../hooks'
import type {TakeFiveHazardAssessmentStepFormValues} from '../../types'
import {StepFormActionsContainer} from '../StepFormActionsContainer'

type Props = {
  readOnly?: boolean
}

export const HazardAssessmentFormFields = ({readOnly = false}: Props) => {
  const {t} = useWidgetTranslation()
  const {control, watch} = useFormContext<TakeFiveHazardAssessmentStepFormValues>()
  const hazards = watch('hazards')
  const isHazardsCommentRequired = !!hazards?.find(({id}) => id === Hazard.Other)
  const risks = watch('risks')
  const isRisksCommentRequired = !!risks?.find(({id}) => id === Risk.Other)

  const hazardOptions = useMemo(
    () =>
      Object.values(Hazard).map((value) => ({
        id: value,
        label: t(`takeFive.hazard.${value}`)
      })),
    [t]
  )

  const riskOptions = useMemo(
    () =>
      Object.values(Risk).map((value) => ({
        id: value,
        label: t(`takeFive.risk.${value}`)
      })),
    [t]
  )

  return (
    <CardBox width={{xs: '100%', md: HROC_CHECKLIST_FORM_WIDTH_IN_PX}}>
      <Stack spacing={2}>
        <ChecklistStepSection title={t('takeFive.label.hazardAssessment')}>
          <TextField
            label={t('label.preparedBy')}
            name="preparedBy"
            readOnly // preparedBy is always read only
            control={control}
          />
        </ChecklistStepSection>
        <ChecklistStepSection
          title={t('takeFive.label.thinkThroughTheTask')}
          subTitle={t('takeFive.label.whatWorkIsToBeDone')}
        >
          <TextField
            label={t('label.yourAnswer')}
            name="workToBeDone"
            readOnly={readOnly}
            control={control}
          />
        </ChecklistStepSection>
        <ChecklistStepSection
          title={t('takeFive.label.assessTheHazards')}
          subTitle={t('takeFive.label.useHazardChecklist')}
        >
          <AutocompleteField
            label={t('takeFive.label.hazards')}
            name="hazards"
            control={control}
            multiple
            readOnly={readOnly}
            options={hazardOptions}
          />
          <TextField
            label={t('label.comment')}
            name="hazardsComment"
            readOnly={readOnly}
            control={control}
            optional={!isHazardsCommentRequired}
          />
        </ChecklistStepSection>
        <ChecklistStepSection
          title={t('takeFive.label.knowTheRisk')}
          subTitle={t('takeFive.label.whatArePotentialConsequences')}
        >
          <AutocompleteField
            label={t('takeFive.label.risks')}
            name="risks"
            control={control}
            multiple
            readOnly={readOnly}
            options={riskOptions}
          />
          <TextField
            label={t('label.comment')}
            name="risksComment"
            readOnly={readOnly}
            control={control}
            optional={!isRisksCommentRequired}
          />
        </ChecklistStepSection>
        <ChecklistStepSection
          title={t('takeFive.label.eliminateOrControlTheHazard')}
          subTitle={t('takeFive.label.howCanIEliminateOrReduceRisk')}
        >
          <TextField
            label={t('label.yourAnswer')}
            name="eliminateControlHazard"
            readOnly={readOnly}
            control={control}
          />
        </ChecklistStepSection>
        <StepFormActionsContainer />
      </Stack>
    </CardBox>
  )
}
